import * as React from 'react';
import { useStyles, useFormControlContext, useDebounceValue } from '@sravni/react-utils';
import { isRange } from '../../../utils/isRange.js';
import { NumberInput } from '../../NumberInput/src/NumberInput.js';
import { Slider } from '../../Slider/src/Slider.js';
import { Space } from '../../Space/src/Space.js';
import modules_4fe38840 from '../styles/SliderInput.module.less.js';

/** Helper function normalizes range value */
const normalizeValue = (value) => {
    if (isRange(value) && (value.from ?? 0) > (value.to ?? 0)) {
        return { from: value.to, to: value.from };
    }
    return value;
};
/** Helper function for checking single mode value */
const isSingleModeValue = (mode, value) => (typeof value === 'number' || !value) && mode === 'single';
/**
 * @description Enter a number within certain range with the mouse or keyboard
 *
 * @component
 * @example
 * ```jsx
 * <SliderInput
 *    placeholder="Название поля"
 *    min={30000}
 *    max={1000000}
 *    postfix="₽/мес"
 *    thousandSeparator=" "
 *    logarithmic
 *  />
 * ```
 */
const SliderInput = React.forwardRef((props, ref) => {
    const { value, min = 0, max = 100, step = 1, invalid, disabled, logarithmic, defaultValue, readOnly, mode = 'single', placeholder, label, className, onChange, onBlur, onFocus, ...restProps } = props;
    const cx = useStyles(modules_4fe38840);
    const formControlContext = useFormControlContext();
    const classNames = cx(className, 'container');
    const isRangeMode = mode === 'range';
    const classNamesInputWrapper = cx({
        range: isRangeMode,
    });
    const classNamesSlider = cx({
        slider: !isRangeMode,
    });
    const [innerValue, setInnerValue] = React.useState(normalizeValue(value || defaultValue));
    const isExternalChangingValue = React.useRef(false);
    const containerRef = React.useRef(null);
    const debouncedInnerValue = useDebounceValue(innerValue || min, 300);
    const isFirstRender = React.useRef(true);
    const handleChangeInput = (value) => {
        isExternalChangingValue.current = false;
        if (isRangeMode) {
            setInnerValue({ ...innerValue, from: value || min });
        }
        else {
            setInnerValue(value);
        }
    };
    const handleChangeInputTo = (value) => {
        isExternalChangingValue.current = false;
        if (isRangeMode && isRange(innerValue)) {
            setInnerValue({ ...innerValue, to: value || max });
        }
    };
    const handleChangeSlider = (value) => {
        isExternalChangingValue.current = false;
        !readOnly && setInnerValue(value);
    };
    const handleBlur = React.useCallback((event) => {
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
            return;
        }
        if (isRange(innerValue) && (innerValue.from ?? 0) > (innerValue.to ?? 0)) {
            // safeInvoke(onChange, normalizeValue(innerValue) || undefined);
            setInnerValue(normalizeValue(innerValue) || undefined);
        }
        onBlur?.(event);
    }, [innerValue, onBlur]);
    React.useEffect(() => {
        let newValue = value;
        if (!newValue) {
            newValue = isFirstRender.current ? defaultValue : value;
        }
        setInnerValue(newValue);
        isFirstRender.current = false;
        isExternalChangingValue.current = true;
    }, [value, defaultValue]);
    React.useEffect(() => {
        !isExternalChangingValue.current && onChange?.(debouncedInnerValue);
    }, [debouncedInnerValue, onChange]);
    return (React.createElement("div", { "data-qa": "SliderInput", className: classNames },
        React.createElement(Space, { ref: containerRef, size: 8, className: classNamesInputWrapper, onBlur: handleBlur, onFocus: onFocus },
            React.createElement(NumberInput, { ...restProps, ref: ref, value: isRange(innerValue) ? innerValue.from : innerValue, min: min, max: max, step: step, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, placeholder: typeof placeholder === 'string' ? placeholder : placeholder?.from, label: typeof label === 'string' ? label : label?.from, readOnly: readOnly, format: undefined, onChange: handleChangeInput }),
            isRangeMode && (React.createElement(NumberInput, { value: isRange(innerValue) ? innerValue.to : '', min: min, max: max, step: step, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, placeholder: typeof placeholder === 'string' ? placeholder : placeholder?.to, label: typeof label === 'string' ? label : label?.to, readOnly: readOnly, format: undefined, onChange: handleChangeInputTo }))),
        isSingleModeValue(mode, innerValue) ? (React.createElement(Slider, { className: classNamesSlider, value: innerValue, min: min, max: max, step: step, mode: "single", disabled: formControlContext.disabled || disabled, logarithmic: logarithmic, onChange: handleChangeSlider })) : (React.createElement(Slider, { className: classNamesSlider, value: innerValue, min: min, max: max, step: step, mode: "range", disabled: formControlContext.disabled || disabled, logarithmic: logarithmic, onChange: handleChangeSlider }))));
});
SliderInput.defaultProps = {
    min: 0,
    max: 100,
    step: 1,
    mode: 'single',
};
// Для этого компонента при указании displayName не отображается автодокументация в сторибуке. Надо разобраться с магией
// SliderInput.displayName = 'SliderInput';

export { SliderInput, normalizeValue };
//# sourceMappingURL=SliderInput.js.map
